import { Button, HSpacer, Input, Text, VSpacer } from '@/components/DesignSystem';
import { AppConfig } from '@/constants/AppConfig';
import { Routes } from '@/constants/Routes';
import { useAuthentication } from '@/contexts/dataSync/AuthenticationContext';
import { AuthApi } from '@/utilities/api/AuthApi';
import { Stack } from '@mui/material';
import { ApiErrors } from '@shared/enums';
import { isValidPhoneNumber, validateEmail } from '@shared/utilities';
import { debounce } from 'lodash';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';

interface LoginEmailAndTelephoneInputProps {
  onConfirmSuccess: ({ lastFour, telephone }: { lastFour: string, telephone: string }) => void,
}

export const LoginEmailAndTelephoneInput = ({
  onConfirmSuccess,
}: LoginEmailAndTelephoneInputProps) => {
  const { t } = useTranslation();
  const { state } = useLocation();
  const { authenticate } = useAuthentication();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [passwordResetRequested, setPasswordResetRequested] = useState(false);
  const [username, setUsername] = useState(state?.username ?? "");
  const [errorMessage, setErrorMessage] = (
    useState<{ title?: string, message: string } | undefined>(state?.errorMessage)
  );

  const { mutate: sendOtpCode, isLoading: isSendingOtp } = useMutation(
    () => {
      return AuthApi.sendOtpCode(
        { telephone: username.replace(/[() -]/g, "") },
      );
    },
    {
      onError: () => {
        setErrorMessage({
          message: t($ => $.auth.errors.notRegistered),
        });
      },
      onSuccess: (data) => {
        setErrorMessage(undefined);
        onConfirmSuccess({ telephone: username, lastFour: data.lastFour });
      },
      retry: false,
    },
  );

  const handleInputChange = useCallback(
    debounce((username: string) => {
      if (isValidPhoneNumber(username.replace(/[() -]/g, ""), AppConfig.env.test)) {
        setErrorMessage(undefined);
        setShowPasswordField(false);
      } else if (validateEmail(username)) {
        setErrorMessage(undefined);
        setShowPasswordField(true);
      } else if (username) {
        setErrorMessage({ message: t($ => $.auth.errors.invalidPhoneOrEmail) });
      } else {
        setErrorMessage(undefined);
      }
    }, 1000),
    [],
  );

  const { mutate: authenticateUser, isLoading: isAuthenticating } = useMutation(
    () => authenticate(username, password),
    {
      onError: (err: { message: string }) => {
        if (err.message === ApiErrors.inactiveAccount) {
          setErrorMessage({ title: t($ => $.auth.errors.inactiveAccount),  message: err.message });
        } else if (err.message === ApiErrors.passwordResetRequired) {
          setErrorMessage({
            title: t($ => $.auth.errors.passwordResetRequired),
            message: t($ => $.auth.errors.passwordResetRequiredMessage),
          });
        } else if (err.message === ApiErrors.tooManyPasswordAttempts) {
          setErrorMessage({
            title: t($ => $.auth.errors.tooManyPasswordAttempts),
            message: t($ => $.auth.errors.tooManyPasswordAttemptsMessage),
          });
        } else {
          setErrorMessage({ message: t($ => $.auth.errors.incorrectCredentials) });
        }
      },
      onSuccess: () => {
        navigate(Routes.HOMEPAGE, { replace: true });
      },
      retry: false,
    },
  );

  const { mutate: requestPasswordReset, isLoading: isRequestingPasswordReset } = useMutation(
    () => AuthApi.requestPasswordReset({ email: username }),
    {
      onError: () => {
        setErrorMessage({
          message: t($ => $.auth.passwordReset.emailSendError),
        });
      },
      onSuccess: () => {
        setErrorMessage(undefined);
        setPasswordResetRequested(true);
      },
      retry: false,
    },
  );

  const handlePasswordReset = () => {
    if (validateEmail(username)) {
      setErrorMessage(undefined);
      requestPasswordReset();
    } else {
      setErrorMessage({ message: t($ => $.auth.passwordReset.emailPrompt) });
    }
  };

  const handleOnContinue = async () => {
    if (isValidPhoneNumber(username.replace(/[() -]/g, ""), AppConfig.env.test)) {
      setErrorMessage(undefined);
      setShowPasswordField(false);
      sendOtpCode();
    } else if (validateEmail(username)) {
      setErrorMessage(undefined);
      setShowPasswordField(true);
      if (password) {
        authenticateUser();
      }
    } else if (username) {
      setErrorMessage({ message: t($ => $.auth.errors.invalidPhoneOrEmail) });
    }
  };

  return (
    <>
      {!!errorMessage && (
        <>
          <Text align="center" category="body-medium" color="error">
            {errorMessage.message}
          </Text>
          <VSpacer size="6" />
        </>
      )}
      <Input
        error={!!errorMessage}
        label={t($ => $.auth.login.emailOrPhonePlaceholder)}
        onChangeText={(username) => {
          setUsername(username);
          handleInputChange(username);
        }}
        onEnterPressed={handleOnContinue}
        testID="login-form-username"
        value={username}
        variant="outlined"
        width="375px"
      />
      {showPasswordField &&
        <>
          <VSpacer size="5" />
          <Stack direction="row" width="375px">
            <Input
              error={!!errorMessage}
              label={t($ => $.auth.login.passwordPlaceholder)}
              onChangeText={setPassword}
              onEnterPressed={handleOnContinue}
              testID="farmer-login-form-password"
              type={showPassword ? undefined : 'password'}
              value={password}
              variant="outlined"
            />
            <HSpacer size="2" />
            <Stack>
              <Button
                onClick={() => setShowPassword(!showPassword)}
                size="small"
                square
                sx={{
                  borderRadius: 1,
                  flex: 1,
                  width: 56,
                }}
                testID="show-password-button"
              >
                {showPassword
                  ? t($ => $.auth.login.passwordHide)
                  : t($ => $.auth.login.passwordShow)}
              </Button>
            </Stack>
          </Stack>
          <VSpacer size="3" />
          <Stack direction="row" justifyContent="flex-end" width="375px">
            <Button
              disabled={isRequestingPasswordReset || isSendingOtp || isAuthenticating}
              loading={isRequestingPasswordReset}
              onClick={handlePasswordReset}
              size="small"
              testID="forgot-password-button"
              variant="text"
            >
              {t($ => $.auth.login.forgotPassword)}
            </Button>
          </Stack>
        </>
      }
      <VSpacer size="8" />
      <Button
        disabled={
          (!validateEmail(username)
            && !isValidPhoneNumber(username.replace(/[() -]/g, ""), AppConfig.env.test))
          || (validateEmail(username) && !password)
        }
        loading={isSendingOtp || isAuthenticating}
        onClick={handleOnContinue}
        testID="login-form-button"
      >
        {t($ => $.common.actions.continue)}
      </Button>
      {passwordResetRequested && (
        <>
          <VSpacer size="4" />
          <Text category="body-small" color="success" textAlign="center">
            {t($ => $.auth.passwordReset.emailSentSuccess)}
          </Text>
        </>
      )}
    </>
  );
};
